export default () => ({
    isDragging: false,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    prevOffsetX: 0,
    prevOffsetY: 0,
    containerWidth: 0,
    containerHeight: 0,
    canvasWidth: 2000,
    canvasHeight: 2000,
    scale: 1,
    minScale: 0.1,
    maxScale: 3,

    init() {
        this.$nextTick(() => {
            setTimeout(() => {
                this.setCanvasSize();
                this.centerCanvas();
                this.setupResizeObserver();
            }, 400);
        });
    },

    setupResizeObserver() {
        const observer = new ResizeObserver(() => {
            this.drawConnectingLines();
        });

        // Observe all nodes in the hierarchy
        const allNodes = this.$el.querySelectorAll('[data-node]');
        allNodes.forEach(node => {
            observer.observe(node);
        });

        observer.observe(this.$refs.canvas);
    },

    setCanvasSize() {
        this.containerWidth = this.$refs.container.offsetWidth;
        this.containerHeight = this.$refs.container.offsetHeight;

        const contentRect = this.getContentRect();

        // Set the canvas size to the content size + some padding
        this.canvasWidth = contentRect.width + 200;
        this.canvasHeight = contentRect.height + 200;
    },

    handleResize() {
        this.$nextTick(() => {
            this.setCanvasSize();
            this.centerCanvas();
            this.drawConnectingLines();
        });
    },

    centerCanvas() {
        // Center the canvas
        this.offsetX = (this.containerWidth - this.canvasWidth) / 2;
        this.offsetY = (this.containerHeight - this.canvasHeight) / 2;
    },

    startDrag(event) {
        this.isDragging = true;
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.prevOffsetX = this.offsetX;
        this.prevOffsetY = this.offsetY;
    },

    startDragTouch(event) {
        this.isDragging = true;
        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
        this.prevOffsetX = this.offsetX;
        this.prevOffsetY = this.offsetY;
    },

    drag(event) {
        if (!this.isDragging) {
            return;
        }

        const deltaX = event.clientX - this.startX;
        const deltaY = event.clientY - this.startY;

        this.offsetX = this.prevOffsetX + deltaX;
        this.offsetY = this.prevOffsetY + deltaY;

        this.drawConnectingLines();
    },

    dragTouch(event) {
        if (!this.isDragging) {
            return;
        }

        const deltaX = event.touches[0].clientX - this.startX;
        const deltaY = event.touches[0].clientY - this.startY;

        this.offsetX = this.prevOffsetX + deltaX;
        this.offsetY = this.prevOffsetY + deltaY;

        this.drawConnectingLines();
    },

    endDrag() {
        this.isDragging = false;
    },

    drawConnectingLines() {
        // Clear previous lines
        const svg = this.$refs.svg;
        while (svg.firstChild) {
            svg.removeChild(svg.firstChild);
        }

        // Draw connections using a recursive approach to handle infinite levels
        this.drawConnectionsForLevel(0, svg);
    },

    drawConnectionsForLevel(level, svg) {
        // Get all nodes at the current level
        const parentNodes = this.$el.querySelectorAll(`[data-node][data-level="${level}"]`);

        // If no nodes at this level, we've reached the end of the hierarchy
        if (parentNodes.length === 0) {
            return;
        }

        // For each parent node, find and connect to its children
        parentNodes.forEach(parentNode => {
            const parentId = parentNode.dataset.id;

            // Find all child nodes at the next level that have this parent ID
            const childNodes = this.$el.querySelectorAll(
                `[data-node][data-level="${level + 1}"][data-parent-id="${parentId}"]`
            );

            // Connect parent to each child
            childNodes.forEach(childNode => {
                this.createLine(parentNode, childNode, svg);
            });
        });

        // Continue to the next level
        this.drawConnectionsForLevel(level + 1, svg);
    },

    createLine(startEl, endEl, svg) {
        // Get relative positions within the canvas instead of using viewport-based positions
        const canvasRect = this.$refs.canvas.getBoundingClientRect();
        const startRect = startEl.getBoundingClientRect();
        const endRect = endEl.getBoundingClientRect();

        // Calculate positions relative to canvas (not container)
        const startX = startRect.left - canvasRect.left + (startRect.width / 2);
        const startY = startRect.bottom - canvasRect.top;
        const endX = endRect.left - canvasRect.left + (endRect.width / 2);
        const endY = endRect.top - canvasRect.top;

        // Create path element for right-angle lines
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

        // Create a path with horizontal and vertical segments only
        // First go down/up to the midpoint between elements, then go horizontally
        const midY = startY + (endY - startY) / 2;
        const pathData = `M ${startX} ${startY}
                          L ${startX} ${midY}
                          L ${endX} ${midY}
                          L ${endX} ${endY}`;

        path.setAttribute('d', pathData);
        path.setAttribute('stroke', '#CBD5E1'); // Tailwind slate-300 color
        path.setAttribute('stroke-width', '2');
        path.setAttribute('fill', 'none');

        svg.appendChild(path);
    },

    getContentRect() {
        // Get all nodes using data-node attribute
        const allNodes = Array.from(this.$el.querySelectorAll('[data-node]'));

        if (!allNodes.length) {
            return {
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                width: this.containerWidth,
                height: this.containerHeight
            };
        }

        // Get all element rectangles
        const rects = allNodes.map(el => el.getBoundingClientRect());

        // Calculate bounding box
        const left = Math.min(...rects.map(r => r.left));
        const top = Math.min(...rects.map(r => r.top));
        const right = Math.max(...rects.map(r => r.right));
        const bottom = Math.max(...rects.map(r => r.bottom));

        return {
            left,
            top,
            right,
            bottom,
            width: right - left,
            height: bottom - top
        };
    }
});
